import { Container } from "inversify";
import { TYPES } from "@utils/inversifyJS/types";
import { CreateJobService } from "@application/useCases/job/CreateJobService";
import {
  IPipelineRepository,
  IRawFileRepository,
  IResultsRepository,
  ISoftwareVersionsRepository,
  IStorageFolderRepository,
  IStorageLocationsRepository,
  IUserRepository,
  IEntitlementsRepository,
} from "@domain/repositories";
import {
  JobRepository,
  LicenseRepository,
  PipelineRepository,
  ResultsRepository,
  SoftwareVersionsRepository,
  StorageFolderRepository,
  StorageLocationsRepository,
  UserRepository,
  EntitlementsRepository,
} from "@infrastructure/repositories";
import { IJobRepository, ISoftwareSettingsRepository } from "@domain/repositories";
import { SoftwareSettingsRepository } from "@infrastructure/repositories/SoftwareSettingsRepository";
import { AuthService, AuthenticatedRequestHandlerService } from "@infrastructure/services";
import { IFastaRepository } from "@domain/repositories/IFastaRepository";
import { FastaRepository } from "@infrastructure/repositories/FastaRepository";
import { UploadFastaService } from "@application/useCases/fasta/UploadFastaService";
import { ListFastaService } from "@application/useCases/fasta/ListFastaService";
import { DeleteFastaService } from "@application/useCases/fasta/DeleteFastaService";
import { DeleteSoftwareSettingsService } from "@application/useCases/softwareSettings/DeleteSoftwareSettingsService";
import { ListJobsService } from "@application/useCases/job/ListJobTasksService";
import { ChangePassword, ListPipelineService } from "@application/useCases";
import { IAuthenticatedRequestHandlerService, IAuthService } from "@application/contracts/services";
import { RawFileRepository } from "@infrastructure/repositories/RawFileRepository";
import { SaveLicenseService } from "@application/useCases/license/SaveLicenseService";
import { ILicenseRepository } from "@domain/repositories/ILicenseRepository";
import { UploadRawFileService } from "@application/useCases/rawFiles/UploadRawFileService";
import { DeleteRawFileService } from "@application/useCases/rawFiles/DeleteRawFileService";
import { UploadLibraryService } from "@application/useCases/library/UploadLibraryService";
import { LibraryRepository } from "@infrastructure/repositories/LibraryRepository";
import { IPermissionsRepository, IProjectRepository, ITeamRepository } from "@domain/repositories";
import { PermissionsRepository, ProjectRepository, TeamRepository } from "@infrastructure/repositories";
import { GetMyPermissionsService } from "@application/useCases/workspace/GetMyPermissionsService";
import { ListProjectsService } from "@application/useCases/workspace/ListProjectsService";
import { ListTeamsService } from "@application/useCases/workspace/ListTeamsService";
import { TeamManagementService } from "@application/useCases/workspace/TeamManagementService";
import { TeamProjectActionsService } from "@application/useCases/workspace/TeamProjectActionsService";
import { ProjectManagementService } from "@application/useCases/workspace/ProjectManagementService";
import { StorageConfigurationService } from "@application/useCases/workspace/StorageConfigurationService";
import { UserDirectoryService } from "@application/useCases/workspace/UserDirectoryService";
import { GetEntitlementsService } from "@application/useCases/workspace/GetEntitlementsService";
import type { IDeploymentConfigurationRepository } from "@domain/repositories";
import { DeploymentConfigurationRepository } from "@infrastructure/repositories/DeploymentConfigurationRepository";
import { DeploymentConfigurationService } from "@application/useCases/workspace/DeploymentConfigurationService";

const createContainer = () => {
  const container = new Container();

  container.bind<CreateJobService>(TYPES.CreateJobService).to(CreateJobService);
  container.bind<IJobRepository>(TYPES.IJobRepository).to(JobRepository);
  container.bind<IPipelineRepository>(TYPES.IPipelineRepository).to(PipelineRepository);
  container.bind<ListPipelineService>(TYPES.ListPipelineService).to(ListPipelineService);
  container.bind<IUserRepository>(TYPES.IUserRepository).to(UserRepository);
  container.bind<ISoftwareSettingsRepository>(TYPES.ISoftwareSettingsRepository).to(SoftwareSettingsRepository);
  container.bind<ISoftwareVersionsRepository>(TYPES.ISoftwareVersionsRepository).to(SoftwareVersionsRepository);
  container.bind<IStorageLocationsRepository>(TYPES.IStorageLocationsRepository).to(StorageLocationsRepository);
  container.bind<IStorageFolderRepository>(TYPES.IStorageFolderRepository).to(StorageFolderRepository);
  container.bind<IAuthenticatedRequestHandlerService>(TYPES.IAuthenticatedRequestHandlerService).to(AuthenticatedRequestHandlerService);
  container.bind<IFastaRepository>(TYPES.IFastaRepository).to(FastaRepository);
  container.bind<UploadFastaService>(TYPES.UploadFastaService).to(UploadFastaService);
  container.bind<ListFastaService>(TYPES.ListFastaService).to(ListFastaService);
  container.bind<ListJobsService>(TYPES.ListJobsService).to(ListJobsService);
  container.bind<IAuthService>(TYPES.IAuthService).to(AuthService).inSingletonScope();
  container.bind<IRawFileRepository>(TYPES.IRawFileRepository).to(RawFileRepository);
  container.bind<SaveLicenseService>(TYPES.SaveLicenseService).to(SaveLicenseService);
  container.bind<ILicenseRepository>(TYPES.ILicenseRepository).to(LicenseRepository);
  container.bind<IResultsRepository>(TYPES.IResultsRepository).to(ResultsRepository);
  container.bind<ChangePassword>(TYPES.ChangePassword).to(ChangePassword);
  container.bind<DeleteFastaService>(TYPES.DeleteFastaService).to(DeleteFastaService);
  container.bind<DeleteSoftwareSettingsService>(TYPES.DeleteSoftwareSettingsService).to(DeleteSoftwareSettingsService);
  container.bind<UploadRawFileService>(TYPES.UploadRawFileService).to(UploadRawFileService).inSingletonScope();
  container.bind<DeleteRawFileService>(TYPES.DeleteRawFileService).to(DeleteRawFileService);
  container.bind<LibraryRepository>(TYPES.LibraryRepository).to(LibraryRepository);
  container.bind<UploadLibraryService>(TYPES.UploadLibraryService).to(UploadLibraryService);
  container.bind<ITeamRepository>(TYPES.ITeamRepository).to(TeamRepository);
  container.bind<IProjectRepository>(TYPES.IProjectRepository).to(ProjectRepository);
  container.bind<IPermissionsRepository>(TYPES.IPermissionsRepository).to(PermissionsRepository);
  container.bind<ListTeamsService>(TYPES.ListTeamsService).to(ListTeamsService);
  container.bind<ListProjectsService>(TYPES.ListProjectsService).to(ListProjectsService);
  container.bind<GetMyPermissionsService>(TYPES.GetMyPermissionsService).to(GetMyPermissionsService);
  container.bind<TeamManagementService>(TYPES.TeamManagementService).to(TeamManagementService);
  container.bind<TeamProjectActionsService>(TYPES.TeamProjectActionsService).to(TeamProjectActionsService);
  container.bind<ProjectManagementService>(TYPES.ProjectManagementService).to(ProjectManagementService);
  container.bind<StorageConfigurationService>(TYPES.StorageConfigurationService).to(StorageConfigurationService);
  container.bind<UserDirectoryService>(TYPES.UserDirectoryService).to(UserDirectoryService);
  container.bind<IEntitlementsRepository>(TYPES.IEntitlementsRepository).to(EntitlementsRepository);
  container.bind<GetEntitlementsService>(TYPES.GetEntitlementsService).to(GetEntitlementsService);
  container.bind<IDeploymentConfigurationRepository>(TYPES.IDeploymentConfigurationRepository).to(DeploymentConfigurationRepository);
  container.bind<DeploymentConfigurationService>(TYPES.DeploymentConfigurationService).to(DeploymentConfigurationService);

  return container;
};

const DIContainer = createContainer();

export { DIContainer };
